import { useNavigate, LoaderFunctionArgs } from 'react-router';
import { requireUser } from '../../../lib/session.server';
import { 
  User, 
  Settings, 
  Shield, 
  Bell, 
  Link2, 
  ChevronRight, 
  KeyRound, 
  Download, 
  Eye, 
  Trash2,
  Sparkles
} from 'lucide-react';
import { Card, CardContent } from '../../../components/ui';

export async function loader({ request }: LoaderFunctionArgs) {
  const user = await requireUser(request);
  return { user };
}

const SETTINGS_SECTIONS = [
  {
    id: 'profile',
    title: 'Profile',
    description: 'Update your personal and professional information',
    icon: User,
    gradient: 'bg-gradient-primary',
    path: '/settings/profile'
  },
  {
    id: 'account',
    title: 'Account',
    description: 'Manage your email, password, and account preferences',
    icon: Settings,
    gradient: 'bg-gradient-secondary',
    path: '/settings/account'
  },
  {
    id: 'security',
    title: 'Security',
    description: 'Password, two-factor authentication, and active sessions',
    icon: Shield,
    gradient: 'bg-gradient-warm',
    path: '/settings/security'
  },
  {
    id: 'notifications',
    title: 'Notifications',
    description: 'Choose how and when you want to be notified',
    icon: Bell,
    gradient: 'bg-gradient-cool',
    path: '/settings/notifications'
  },
  {
    id: 'connections',
    title: 'Connections',
    description: 'Manage job board accounts and integrations',
    icon: Link2,
    gradient: 'bg-gradient-primary',
    path: '/settings/connections'
  }
];

export function SettingsIndexPage() {
  const navigate = useNavigate();

  const quickActions = [
    {
      title: 'Change Password',
      icon: KeyRound,
      action: () => navigate('/settings/security'),
      color: 'text-primary'
    },
    {
      title: 'Notifications',
      icon: Bell,
      action: () => navigate('/settings/notifications'),
      color: 'text-primary'
    },
    {
      title: 'Add Connection',
      icon: Link2,
      action: () => navigate('/settings/connections'),
      color: 'text-primary'
    },
    {
      title: 'Privacy Settings',
      icon: Eye,
      action: () => alert('Privacy settings coming soon'),
      color: 'text-primary'
    },
    {
      title: 'Export Data',
      icon: Download,
      action: () => alert('Data export coming soon'),
      color: 'text-primary'
    },
    {
      title: 'Delete Account',
      icon: Trash2,
      action: () => navigate('/settings/account'),
      color: 'text-destructive'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-sage-50/30 to-timberwolf-50/50">
      <div className="max-w-6xl mx-auto px-6 py-8 space-y-8">
        {/* Enhanced Header */}
        <div className="relative overflow-hidden bg-gradient-subtle rounded-2xl p-8 border border-border/50">
          <div className="absolute inset-0 bg-gradient-primary opacity-[0.02]" />
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-warm opacity-[0.05] rounded-full -mr-32 -mt-32" />
          <div className="relative flex items-center gap-4">
            <div className="p-3 bg-gradient-primary rounded-xl shadow-lg">
              <Settings className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-bold tracking-tight text-foreground">Settings</h1>
              <p className="text-muted-foreground mt-2 text-lg">
                Manage your account, preferences, and integrations
              </p>
            </div>
          </div>
        </div>

        {/* Main Settings Sections */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {SETTINGS_SECTIONS.map((section) => {
            const Icon = section.icon;
            return (
              <Card
                key={section.id}
                className="group cursor-pointer border-0 hover:-translate-y-2 transition-all duration-300"
                onClick={() => navigate(section.path)}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className={`p-3 rounded-xl ${section.gradient} shadow-lg group-hover:shadow-xl transition-shadow`}>
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                    <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2 text-foreground group-hover:text-primary transition-colors">
                      {section.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {section.description}
                    </p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Enhanced Quick Actions */}
        <Card className="border-0 bg-gradient-subtle">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-gradient-secondary rounded-xl">
                <Sparkles className="h-5 w-5 text-white" />
              </div>
              <h2 className="text-2xl font-semibold text-foreground">Quick Actions</h2>
            </div>
            
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {quickActions.map((action, index) => {
                const Icon = action.icon;
                return (
                  <button
                    key={index}
                    onClick={action.action}
                    className="group p-4 bg-white/80 backdrop-blur-sm border border-border/50 rounded-xl hover:bg-white hover:shadow-md transition-all duration-200 text-left"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg bg-gradient-subtle group-hover:bg-gradient-primary transition-all ${action.color === 'text-destructive' ? 'group-hover:bg-gradient-to-r group-hover:from-destructive group-hover:to-red-600' : ''}`}>
                        <Icon className={`h-4 w-4 ${action.color} group-hover:text-white transition-colors`} />
                      </div>
                      <span className={`text-sm font-medium ${action.color} group-hover:text-foreground transition-colors`}>
                        {action.title}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}